/**
 * Eros PLP filter tray (ADR-205) - DOM RENDERING ONLY. Takes a plain view-model built by
 * eros-plp-filter-tray.js (via eros-plp-tray-logic.js) and draws it; reports taps through
 * handler callbacks. It never reads or writes the facet form and never decides what to show.
 *
 * View-model:
 *   vm = { open, count, countText, chips:[{label,kind,param,value}], rows:[row], ui:{...},
 *          stock:{label,active}|null }
 *   row = { id, title, folded, expanded, summary, kind:'options'|'brand'|'price',
 *           options:[{value,label,count,active,dim,image,iconGroup,iconValue,symbol,tone}],
 *           price:{ buckets:[{label,glyph,active}], sale:{label,active}|null },
 *           search:{ label, placeholder, open, query, focus }|null }   (brand + actives rows)
 *   handlers = { toggleOpen, close, clearAll, option(rowId,opt), bucket(i), stock(), sale(),
 *                chip(chip), expand(rowId), rowSearch(rowId), rowQuery(rowId,q), rowVm(rowId) }
 *
 * The bar («فلاتر» / available-only chip / close / clear) is drawn into `host` (the slot inside
 * the collection toolbar, next to the sort button); the layer, chips and count go into rootEl
 * (in-flow, above the grid). Without a host the bar falls back to the top of rootEl.
 */
(function (root) {
  'use strict';
  var ns = (root.ErosPlpTray = root.ErosPlpTray || {});
  var icons = function () {
    return ns.icons;
  };

  var SVG_FILTER =
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"><path d="M10 18h4"/><path d="M3 6h18"/><path d="M6 12h12"/></svg>';
  var SVG_UP = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="currentColor"><path d="M7 14l5-5 5 5z"/></svg>';
  var SVG_CHEVRON =
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>';
  var SVG_TICK =
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>';
  var SVG_X =
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>';

  function el(tag, cls, attrs, html) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (attrs) for (var k in attrs) if (Object.prototype.hasOwnProperty.call(attrs, k) && attrs[k] !== null) n.setAttribute(k, attrs[k]);
    if (html !== undefined) n.innerHTML = html;
    return n;
  }
  function text(tag, cls, str, attrs) {
    var n = el(tag, cls, attrs);
    n.textContent = str;
    return n;
  }
  function fill(s, map) {
    return String(s || '').replace(/\{(\w+)\}/g, function (m, k) {
      return map[k] === undefined ? m : map[k];
    });
  }

  /* ---- one circle ---------------------------------------------------------------------- */
  function circle(o) {
    /* o: { label, active, dim, onTap, svg, image, glyph, kind, tone } - tone tints a symbol circle */
    var b = el('button', 'eros-tray__opt' + (o.kind ? ' eros-tray__opt--' + o.kind : ''), {
      type: 'button',
      'aria-pressed': o.active ? 'true' : 'false'
    });
    if (o.dim) b.setAttribute('data-dim', '1');
    var c = el('span', 'eros-tray__circle');
    if (o.tone) c.setAttribute('data-tone', o.tone);
    if (o.image) {
      var img = el('img', 'eros-tray__logo', { src: o.image, alt: '', loading: 'lazy', decoding: 'async', width: 48, height: 48 });
      c.appendChild(img);
    } else if (o.glyph) {
      c.appendChild(
        text('span', 'eros-tray__glyph' + (o.tone ? ' eros-tray__glyph--symbol' : '') + (o.tone && o.glyph.length > 2 ? ' eros-tray__glyph--long' : ''), o.glyph)
      );
    } else if (o.svg) {
      c.innerHTML = o.svg;
    } else {
      c.appendChild(text('span', 'eros-tray__glyph', String(o.label || '').charAt(0)));
    }
    if (o.active) c.appendChild(el('span', 'eros-tray__tick', null, SVG_TICK));
    b.appendChild(c);
    b.appendChild(text('span', 'eros-tray__label', o.label));
    b.addEventListener('click', o.onTap);
    return b;
  }

  /* ---- tracks (the horizontal scrolling rows) ----------------------------------------- */
  function optionIcon(row, opt) {
    if (row.id === 'safe_for_pregnancy') return icons().svg('safety', 'pregnancy');
    if (row.id === 'safe_for_sensitive') return icons().svg('safety', 'sensitive');
    return icons().svg(opt.iconGroup, opt.iconValue);
  }

  /* Active-ingredient options are drawn as a tinted symbol circle, never as an icon. */
  function symbolled(row, opt) {
    return row.id === 'actives' && !!opt.symbol;
  }

  function fillTrack(track, row, h, vm) {
    track.textContent = '';
    var i;
    if (row.search) {
      var s = row.search;
      track.appendChild(
        circle({
          label: s.label,
          active: !!s.open,
          kind: 'search',
          svg: icons().svg('search'),
          onTap: function () {
            h.rowSearch(row.id);
          }
        })
      );
      if (s.open) {
        var inp = el('input', 'eros-tray__search', {
          type: 'search',
          placeholder: s.placeholder,
          'aria-label': s.label,
          autocomplete: 'off',
          enterkeyhint: 'search'
        });
        inp.value = s.query || '';
        inp.addEventListener('input', function () {
          h.rowQuery(row.id, inp.value);
        });
        track.appendChild(inp);
        if (s.focus) {
          setTimeout(function () {
            inp.focus();
          }, 0);
        }
      }
    }
    if (row.kind === 'price') {
      var p = row.price;
      for (i = 0; i < p.buckets.length; i++) {
        (function (idx) {
          var bk = p.buckets[idx];
          track.appendChild(
            circle({ label: bk.label, active: bk.active, glyph: bk.glyph, kind: 'price', onTap: function () { h.bucket(idx); } })
          );
        })(i);
      }
      if (p.sale) {
        track.appendChild(circle({ label: p.sale.label, active: p.sale.active, svg: icons().svg('sale'), onTap: h.sale }));
      }
      return;
    }
    var opts = row.options || [];
    for (i = 0; i < opts.length; i++) {
      (function (opt) {
        track.appendChild(
          circle({
            label: opt.label,
            active: opt.active,
            dim: opt.dim,
            image: row.kind === 'brand' ? opt.image : '',
            svg: symbolled(row, opt) || (row.kind === 'brand' && opt.image) ? '' : optionIcon(row, opt),
            glyph: symbolled(row, opt) ? opt.symbol : row.kind === 'brand' && !opt.image ? opt.label.charAt(0).toUpperCase() : '',
            tone: symbolled(row, opt) ? opt.tone : '',
            onTap: function () {
              h.option(row.id, opt);
            }
          })
        );
      })(opts[i]);
    }
  }

  function makeTrack(row, h, vm) {
    var t = el('div', 'eros-tray__track', { role: 'group', 'aria-label': row.title, 'data-row': row.id });
    fillTrack(t, row, h, vm);
    t.addEventListener(
      'scroll',
      function () {
        scrollPos[row.id] = t.scrollLeft;
      },
      { passive: true }
    );
    return t;
  }

  /* ---- rows ---------------------------------------------------------------------------- */
  function openRow(row, h, vm) {
    var s = el('section', 'eros-tray__row', { 'data-row-id': row.id });
    s.appendChild(text('h3', 'eros-tray__row-title', row.title));
    s.appendChild(makeTrack(row, h, vm));
    return s;
  }

  function foldedRow(row, h, vm) {
    var wrap = el('section', 'eros-tray__fold', { 'data-row-id': row.id, 'data-expanded': row.expanded ? '1' : '0' });
    var head = el('button', 'eros-tray__fold-head', { type: 'button', 'aria-expanded': row.expanded ? 'true' : 'false' });
    head.appendChild(text('span', 'eros-tray__fold-title', row.title));
    head.appendChild(text('span', 'eros-tray__fold-summary', row.summary || ''));
    head.appendChild(el('span', 'eros-tray__fold-chev', null, SVG_CHEVRON));
    var body = el('div', 'eros-tray__fold-body');
    var inner = el('div', 'eros-tray__fold-inner');
    body.appendChild(inner);
    var built = false;
    function ensure(rvm) {
      if (built) return;
      built = true;
      inner.appendChild(makeTrack(rvm, h, vm));
    }
    if (row.expanded) ensure(row);
    head.addEventListener('click', function () {
      var open = wrap.getAttribute('data-expanded') !== '1';
      if (open) ensure(h.rowVm(row.id) || row);
      wrap.setAttribute('data-expanded', open ? '1' : '0');
      head.setAttribute('aria-expanded', open ? 'true' : 'false');
      h.expand(row.id, open);
    });
    wrap.appendChild(head);
    wrap.appendChild(body);
    return wrap;
  }

  /* ---- chips + bar --------------------------------------------------------------------- */
  function chipsEl(vm, h) {
    var box = el('div', 'eros-tray__chips', { role: 'list' });
    for (var i = 0; i < vm.chips.length; i++) {
      (function (chip) {
        var c = el('button', 'eros-tray__chip', {
          type: 'button',
          role: 'listitem',
          'aria-label': fill(vm.ui.remove, { name: chip.label })
        });
        c.appendChild(text('span', 'eros-tray__chip-text', chip.label));
        c.appendChild(el('span', 'eros-tray__chip-x', null, SVG_X));
        c.addEventListener('click', function () {
          h.chip(chip);
        });
        box.appendChild(c);
      })(vm.chips[i]);
    }
    return box;
  }

  function barEl(vm, h) {
    var bar = el('div', 'eros-tray__bar');
    var f = el('button', 'eros-tray__filters', { type: 'button', 'aria-expanded': vm.open ? 'true' : 'false' });
    /* both glyphs are drawn; CSS shows the up-chevron instead of the filter icon on phones while
       the tray is open (the separate close button is hidden there to keep one row) */
    f.appendChild(el('span', 'eros-tray__filters-icon', null, SVG_FILTER));
    f.appendChild(el('span', 'eros-tray__filters-up', null, SVG_UP));
    f.appendChild(text('span', 'eros-tray__filters-text', vm.ui.filters + (vm.count > 0 ? ' (' + vm.count + ')' : '')));
    f.addEventListener('click', h.toggleOpen);
    bar.appendChild(f);
    if (vm.stock) {
      var st = el('button', 'eros-tray__stock', { type: 'button', 'aria-pressed': vm.stock.active ? 'true' : 'false' });
      if (vm.stock.active) st.appendChild(el('span', 'eros-tray__stock-tick', null, SVG_TICK));
      st.appendChild(text('span', 'eros-tray__stock-text', vm.stock.label));
      st.addEventListener('click', h.stock);
      bar.appendChild(st);
    }
    var up = el('button', 'eros-tray__close', { type: 'button', 'aria-label': vm.ui.close }, SVG_UP);
    if (!vm.open) up.hidden = true;
    up.addEventListener('click', h.close);
    bar.appendChild(up);
    /* the label collapses to an X on very narrow phones (CSS), the aria-label keeps the full name */
    var clr = el('button', 'eros-tray__clear', { type: 'button', 'aria-label': vm.ui.clear_all });
    clr.appendChild(text('span', 'eros-tray__clear-text', vm.ui.clear_all));
    clr.appendChild(el('span', 'eros-tray__clear-x', null, SVG_X));
    if (vm.count < 1) clr.hidden = true;
    clr.addEventListener('click', h.clearAll);
    bar.appendChild(clr);
    return bar;
  }

  /* ---- public: render ------------------------------------------------------------------ */
  /* Horizontal scroll offset of every row, kept at module scope: theme.js rebuilds the whole
     section (and so the tray) after each facet:update, and a long row must not jump back. */
  var scrollPos = {};

  function render(rootEl, vm, h, host) {
    var wasOpen = rootEl.getAttribute('data-open') === '1';
    var keep = [];
    var data = rootEl.querySelector('script[data-eros-plp-tray-data]');
    if (data) keep.push(data);
    rootEl.textContent = '';
    for (var k = 0; k < keep.length; k++) rootEl.appendChild(keep[k]);
    rootEl.setAttribute('data-open', vm.open ? '1' : '0');

    if (host) {
      host.textContent = '';
      host.appendChild(barEl(vm, h));
    } else {
      rootEl.appendChild(barEl(vm, h));
    }

    var layer = el('div', 'eros-tray__layer', { 'data-eros-tray-layer': '', 'data-open': vm.open && (wasOpen || vm.instant) ? '1' : '0' });
    if (!vm.open) layer.setAttribute('aria-hidden', 'true');
    var inner = el('div', 'eros-tray__layer-inner');
    layer.appendChild(inner);
    if (vm.open) {
      for (var i = 0; i < vm.rows.length; i++) {
        var row = vm.rows[i];
        inner.appendChild(row.folded ? foldedRow(row, h, vm) : openRow(row, h, vm));
      }
    }
    rootEl.appendChild(layer);
    /* restore horizontal scroll positions of the rebuilt tracks (needs the layer attached) */
    var tracks = inner.querySelectorAll('.eros-tray__track');
    for (var t = 0; t < tracks.length; t++) {
      var pos = scrollPos[tracks[t].getAttribute('data-row')];
      if (pos) tracks[t].scrollLeft = pos;
    }
    if (vm.open && !wasOpen && !vm.instant) {
      /* attached with data-open=0: force a reflow, then flip - the grid-rows transition plays */
      void layer.offsetHeight;
      layer.setAttribute('data-open', '1');
    }

    if (!vm.open && vm.chips.length) rootEl.appendChild(chipsEl(vm, h));
    rootEl.appendChild(text('p', 'eros-tray__count', vm.countText, { 'aria-live': 'polite' }));
  }

  /** Rebuild just one track (brand search typing keeps focus in the input). */
  function refreshTrack(rootEl, rowVm, h, vm) {
    var track = rootEl.querySelector('.eros-tray__track[data-row="' + rowVm.id + '"]');
    if (!track) return;
    var input = track.querySelector('.eros-tray__search');
    var hadFocus = input && document.activeElement === input;
    var caret = hadFocus ? input.selectionStart : 0;
    fillTrack(track, rowVm, h, vm);
    if (hadFocus) {
      var again = track.querySelector('.eros-tray__search');
      if (again) {
        again.focus();
        try {
          again.setSelectionRange(caret, caret);
        } catch (e) {
          /* search inputs may not support selection */
        }
      }
    }
  }

  ns.view = { render: render, refreshTrack: refreshTrack };
})(typeof window !== 'undefined' ? window : globalThis);
